import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/profile-components/Navbar";
import Topbar from "../components/profile-components/Topbar";
import API_BASE_URL from "../config";
import useCurrentUserAccess from "../hooks/useCurrentUserAccess";
import "./SalesPages.css";

function Users() {
  const navigate = useNavigate();
  const { loading: accessLoading, platformRole, account, accountRole } = useCurrentUserAccess();

  const isPlatformAdmin = platformRole?.key_name === "platform_admin";
  const isAccountAdmin = accountRole?.key_name === "account_admin";

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  const pageMode = useMemo(() => {
    if (isPlatformAdmin) return "platform";
    if (isAccountAdmin) return "subscriber";
    return "none";
  }, [isPlatformAdmin, isAccountAdmin]);

  useEffect(() => {
    if (!accessLoading && pageMode !== "none") {
      loadUsers();
    } else if (!accessLoading && pageMode === "none") {
      setLoading(false);
      setUsers([]);
    }
  }, [accessLoading, pageMode]);

  const loadUsers = async () => {
    try {
      setLoading(true);

      const token = localStorage.getItem("token");
      const endpoint =
        pageMode === "platform"
          ? `${API_BASE_URL}/api/users/admin/list`
          : `${API_BASE_URL}/api/users/account/list`;

      const res = await fetch(endpoint, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (!res.ok) {
        throw new Error("Failed to load users");
      }

      const data = await res.json();
      setUsers(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Load users error:", err.message);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = useMemo(() => {
    const term = searchTerm.toLowerCase().trim();
    if (!term) return users;

    return users.filter((user) =>
      [
        user.name,
        user.email,
        user.phone,
        user.platform_role_label,
        user.platform_role_key,
        user.account_role_label,
        user.account_role_key,
        user.account_name
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(term))
    );
  }, [users, searchTerm]);

  const getUserType = (user) => {
    if (user.platform_role_key) return "Platform User";
    if (user.account_role_key) return "Subscriber User";
    return "Unassigned";
  };

  const getRoleLabel = (user) => {
    return user.platform_role_label || user.account_role_label || "No Role";
  };

  const getAccountLabel = (user) => {
    return user.account_name || "—";
  };

  const getStatusLabel = (user) => {
    if (user.platform_role_key) return "Active";
    if (user.is_active === 1 || user.is_active === true) return "Active";
    if (user.is_active === 0) return "Inactive";
    return "—";
  };

  const statusClass = (value) =>
    `sales-chip ${String(value || "").toLowerCase().replace(/\s+/g, "-")}`;

  const pageTitle =
    pageMode === "platform" ? "Platform Users" :
    pageMode === "subscriber" ? "Users" :
    "Users";

  const pageSubtitle =
    pageMode === "platform"
      ? "Manage internal platform users and platform access."
      : pageMode === "subscriber"
      ? `Manage users for ${account?.business_name || account?.name || "your account"}.`
      : "You do not have access to view users here.";

  const tableHeading =
    pageMode === "platform" ? "Platform Users" : "Account Users";

  const searchPlaceholder =
    pageMode === "platform"
      ? "Search by name, email, phone, or platform role..."
      : "Search by name, email, phone, or account role...";

  if (accessLoading) {
    return (
      <>
        <Navbar />
        <Topbar />
        <div className="main-content sales-page">
          <div className="sales-section-card">Loading access...</div>
        </div>
      </>
    );
  }

  if (pageMode === "none") {
    return (
      <>
        <Navbar />
        <Topbar />
        <div className="main-content sales-page">
          <div className="sales-section-card">
            You do not have access to view users.
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <Topbar />

      <div className="main-content sales-page">
        <div className="sales-hero">
          <div>
            <p className="sales-eyebrow">Agent Book CRM</p>
            <h1 className="sales-title">{pageTitle}</h1>
            <p className="sales-subtitle">{pageSubtitle}</p>
          </div>

          <div className="sales-header-actions">
            <button
              className="sales-primary-btn"
              onClick={() => navigate("/users/new")}
            >
              + New User
            </button>
          </div>
        </div>

        <div className="sales-toolbar-card">
          <input
            type="text"
            className="sales-search-input"
            placeholder={searchPlaceholder}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="sales-table-card">
          <div className="sales-table-header">
            <div>
              <h2>{tableHeading}</h2>
              <p>{filteredUsers.length} user{filteredUsers.length === 1 ? "" : "s"}</p>
            </div>
          </div>

          <div className="sales-table-wrap">
            <table className="sales-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Type</th>
                  <th>Role</th>
                  <th>Subscriber</th>
                  <th>Phone</th>
                  <th>Status</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="6" className="sales-empty-cell">
                      Loading users...
                    </td>
                  </tr>
                ) : filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="sales-empty-cell">
                      No users found.
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map((user) => (
                    <tr
                      key={`${user.id}-${user.account_id || "platform"}`}
                      className="sales-click-row"
                      onClick={() => navigate(`/users/${user.id}`)}
                    >
                      <td>
                        <div className="sales-primary-cell">
                          <div className="sales-name">{user.name || "No Name"}</div>
                          <div className="sales-secondary">{user.email || "No email"}</div>
                        </div>
                      </td>

                      <td>{getUserType(user)}</td>
                      <td>{getRoleLabel(user)}</td>
                      <td>{getAccountLabel(user)}</td>
                      <td>{user.phone || "—"}</td>
                      <td>
                        <span className={statusClass(getStatusLabel(user))}>
                          {getStatusLabel(user)}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}

export default Users;